import {
	SECTION_SPEC_STATUS,
	type SectionSpecDetail,
} from "@/shared/models/SectionSpec";

export type SectionSpecState =
	| { kind: "empty" }
	| { kind: "scheduled"; scheduledId: number; canSendToDraft: boolean }
	| { kind: "draft"; draftId: number }
	| {
			kind: "published";
			currentId: number;
			canEditPublished: boolean;
			backToDraftBlockReason: string | null;
	  };

export const hasSectionSpecSubRevisions = (
	detail: SectionSpecDetail,
	profileSpecId: number,
) => {
	const version = detail.versions.find(
		(row) => row.ProfileSpecId === profileSpecId,
	);
	return (
		version?.Rev != null &&
		detail.versions.some(
			(row) =>
				row.ProfileSpecId !== profileSpecId &&
				row.Rev === version.Rev &&
				row.StatusCode !== SECTION_SPEC_STATUS.DRAFT,
		)
	);
};

export const getSectionSpecState = (
	detail: SectionSpecDetail,
	hasEditPublishedPermission: boolean,
): SectionSpecState => {
	const metadata = detail.metadata;
	const draftId = metadata?.ActiveDraftProfileSpecId ?? null;
	const scheduledId = metadata?.NextScheduledPublishedProfileSpecId ?? null;
	const currentId = metadata?.CurrentPublishedProfileSpecId ?? null;

	if (scheduledId !== null)
		return { kind: "scheduled", scheduledId, canSendToDraft: draftId === null };
	if (draftId !== null) return { kind: "draft", draftId };
	if (currentId === null) return { kind: "empty" };

	const inspectionCount = detail.spec?.InspectionCount ?? 0;
	return {
		kind: "published",
		currentId,
		canEditPublished:
			hasEditPublishedPermission &&
			metadata?.LatestConfirmedProfileSpecId === currentId,
		backToDraftBlockReason:
			inspectionCount > 0
				? `This revision has ${inspectionCount} inspection(s) and cannot be sent back to Draft.`
				: hasSectionSpecSubRevisions(detail, currentId)
					? "This revision has sub-revisions and cannot be sent back to Draft."
					: null,
	};
};

export const getDefaultProfileSpecId = (detail: SectionSpecDetail) =>
	detail.metadata?.CurrentPublishedProfileSpecId ??
	detail.metadata?.NextScheduledPublishedProfileSpecId ??
	detail.metadata?.ActiveDraftProfileSpecId ??
	(detail.versions.length === 1 ? detail.versions[0].ProfileSpecId : null);
